import { useEffect, useRef } from 'react';
import { useToast } from '@/components/ui/use-toast';

// Global flag to prevent showing multiple refresh toasts within a short time window
let lastToastTime = 0;
const TOAST_DEBOUNCE_MS = 60000; // Only show one toast per 60 seconds globally

/**
 * Hook to detect when loading states are stuck and suggest browser refresh
 * 
 * @param {boolean} isLoading - The loading state to monitor
 * @param {number} timeoutMs - Time in milliseconds before showing refresh message (default: 20000 = 20 seconds)
 * @param {boolean} enabled - Whether to enable the stuck detection (default: true)
 */
export function useStuckLoadingDetector(isLoading, timeoutMs = 20000, enabled = true) {
  const { toast } = useToast();
  const timeoutRef = useRef(null);
  const hasShownMessageRef = useRef(false);

  useEffect(() => {
    // Don't run if disabled
    if (!enabled) {
      return;
    }

    // If loading started, set up a timeout to show refresh message
    if (isLoading && !hasShownMessageRef.current) {
      timeoutRef.current = setTimeout(() => {
        const now = Date.now();
        // Only show toast if enough time has passed since the last one (global debounce)
        if (now - lastToastTime >= TOAST_DEBOUNCE_MS) {
          hasShownMessageRef.current = true;
          lastToastTime = now;
          toast({
            title: "Loading is taking longer than expected",
            description: "If the page seems stuck, please refresh your browser to fix any connection issues. Press F5 or click the refresh button.",
            variant: "default",
            duration: 12000, // Show for 12 seconds to give users time to read
          });
        }
      }, timeoutMs);
    }

    // If loading finished, clear the timeout and reset the flag
    if (!isLoading) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      hasShownMessageRef.current = false;
    }

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoading, timeoutMs, enabled, toast]);
}
